import httpClient from '../../../api/httpClient';
import { 
  ItemInventory, 
  ItemInventoryFilterParams,
  ItemInventoryFormData
} from '../types/inventory.types';

export const inventoryApi = {
  /**
   * Get paginated list of inventory transactions
   */
  getInventoryTransactions: async (params: ItemInventoryFilterParams): Promise<ItemInventory[]> => {
    const response = await httpClient.get<ItemInventory[]>('/inventory', { params });
    return response.data;
  },

  /**
   * Get inventory transactions by type
   */
  getInventoryTransactionsByType: async (type: string, includeDetails: boolean = true): Promise<ItemInventory[]> => {
    const response = await httpClient.get<ItemInventory[]>(`/inventory/types/${type}`, {
      params: { includeDetails }
    });
    return response.data;
  },

  /**
   * Get inventory transactions by location
   */
  getInventoryTransactionsByLocation: async (locationId: string, includeDetails: boolean = true): Promise<ItemInventory[]> => {
    const response = await httpClient.get<ItemInventory[]>(`/inventory/locations/${locationId}`, {
      params: { includeDetails }
    });
    return response.data;
  },

  /**
   * Get inventory transactions by date range
   */
  getInventoryTransactionsByDateRange: async (
    startDate: string, 
    endDate: string, 
    includeDetails: boolean = true
  ): Promise<ItemInventory[]> => {
    const response = await httpClient.get<ItemInventory[]>('/inventory/date-range', {
      params: { startDate, endDate, includeDetails }
    });
    return response.data;
  },

  /**
   * Get a single inventory transaction by ID
   */
  getInventoryTransactionById: async (id: string, includeDetails: boolean = true): Promise<ItemInventory> => {
    const response = await httpClient.get<ItemInventory>(`/inventory/${id}`, {
      params: { includeDetails }
    });
    return response.data;
  },

  /**
   * Create a new inventory transaction
   */
  createInventoryTransaction: async (data: ItemInventoryFormData): Promise<ItemInventory> => {
    try {
      console.log('Sending inventory transaction data:', JSON.stringify(data, null, 2));
      const response = await httpClient.post<ItemInventory>('/inventory', data, {
        validateStatus: (status) => status < 500
      });
      
      if (response.status >= 400) {
        console.error('Validation error from server:', response.data);
        const errorMessage = (response.data as any)?.message || 'Validation failed';
        throw new Error(errorMessage);
      }
      
      return response.data;
    } catch (error: any) {
      const errorDetails = {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
      };
      console.error('Create inventory transaction error details:', errorDetails);
      
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      
      throw error;
    }
  },

  /**
   * Update an existing inventory transaction (DRAFT only)
   */
  updateInventoryTransaction: async (id: string, data: Partial<ItemInventoryFormData>): Promise<ItemInventory> => {
    try {
      console.log('Updating inventory transaction:', id, JSON.stringify(data, null, 2));
      const response = await httpClient.patch<ItemInventory>(`/inventory/${id}`, data, {
        validateStatus: (status) => status < 500
      });
      
      if (response.status >= 400) {
        console.error('Validation error from server:', response.data);
        const errorMessage = (response.data as any)?.message || 'Validation failed';
        throw new Error(errorMessage);
      }
      
      return response.data;
    } catch (error: any) {
      const errorDetails = {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
      };
      console.error('Update inventory transaction error details:', errorDetails);
      
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      
      throw error;
    }
  },

  /**
   * Approve an inventory transaction
   */
  approveInventoryTransaction: async (id: string): Promise<ItemInventory> => {
    try {
      console.log('🔥 Approving inventory transaction:', id);
      const response = await httpClient.patch<ItemInventory>(`/inventory/${id}/approve`, {}, {
        validateStatus: (status) => status < 500
      });
      
      if (response.status >= 400) {
        console.error('Approval error from server:', response.data);
        const errorMessage = (response.data as any)?.message || 'Approval failed';
        throw new Error(errorMessage);
      }
      
      console.log('✅ Transaction approved successfully');
      return response.data;
    } catch (error: any) {
      console.error('Approve inventory transaction error:', error.response?.data);
      
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      
      throw error;
    }
  },

  /**
   * Cancel an inventory transaction
   */
  cancelInventoryTransaction: async (id: string): Promise<ItemInventory> => {
    const response = await httpClient.patch<ItemInventory>(`/inventory/${id}/cancel`);
    return response.data;
  },

  /**
   * Delete an inventory transaction (soft delete)
   */
  deleteInventoryTransaction: async (id: string): Promise<ItemInventory> => {
    const response = await httpClient.delete<ItemInventory>(`/inventory/${id}`);
    return response.data;
  },

  /**
   * Get inventory summary for a location
   */
  getLocationSummary: async (locationId: string): Promise<any> => {
    const response = await httpClient.get(`/inventory/summary/location/${locationId}`);
    return response.data;
  },
};
